import React, { useState, useEffect } from 'react';
import { NavLink, useParams } from 'react-router-dom';

// Components
import ImageSkeleton from '../../components/Skeleton/ImageSkeleton';
import TextSkeleton from '../../components/Skeleton/TextSkeleton';

// Assets
import GascoIcoClock from '../../assets/images/ico-clock.svg';
import GascoIcoRightBlue from '../../assets/images/ico-right-blue.svg';

// Services
import { getArticuloBySlug, getArticulosPopulares } from '../../config/services/strapi/strapi';

// Types
import { Articulo } from '../../interfaces/types';

import { formatearFechaLarga } from '../../helpers/fecha';

function DetalleArticulo() {
  const { slug } = useParams();

  const [cargando, setCargando] = useState({
    articulo: true,
    populares: true,
  });
  const [articulo, setArticulo] = useState<Articulo | null>(null);
  const [populares, setPopulares] = useState<Articulo[]>([]);

  const tipoContenido = {
    textoEnriquecido: 'shared.rich-text',
    imagen: 'shared.media',
  }

  const copiarEnlace = () => {
    navigator.clipboard.writeText(window.location.href);
    window.alert('Enlace copiado a su portapapeles');
  };

  useEffect(() => {
    const fetchArticulo = async () => {
      setCargando(prev => ({ ...prev, articulo: true }));

      if (slug) {
        const fetchedArticulo = await getArticuloBySlug(slug);

        setArticulo({
          id: fetchedArticulo.id,
          titulo: fetchedArticulo.titulo,
          autor: fetchedArticulo.autor,
          fecha: fetchedArticulo.fecha,
          categoria: fetchedArticulo.categoria,
          portadaUrl: fetchedArticulo.imagen_cubierta.url,
          minutosLectura: fetchedArticulo.minutos_lectura,
          slug: fetchedArticulo.slug,
          contenido: fetchedArticulo.contenido,
          descripcion: fetchedArticulo.descripcion,
          etiqueta: fetchedArticulo.etiqueta,
        });

        document.title = `${fetchedArticulo.titulo} - Gasco Soluciones Energéticas para Empresas`;
      }

      setCargando(prev => ({ ...prev, articulo: false }));
    };

    const fetchArticulosPopulares = async () => {
      setCargando(prev => ({ ...prev, populares: true })); 

      const rawPopulares = await getArticulosPopulares(slug || '');
      const mappedPopulares: Articulo[] = rawPopulares.map((popular: any) => ({
        id: popular.id,
        titulo: popular.titulo,
        autor: popular.autor,
        categoria: popular.categoria,
        fecha: popular.fecha,
        contenido: popular.contenido,
        descripcion: popular.descripcion,
        portadaUrl: popular.imagen_cubierta.url,
        minutosLectura: popular.minutos_lectura,
        slug: popular.slug,
        etiqueta: popular.etiqueta,
      }));

      setPopulares(mappedPopulares);
      setCargando(prev => ({ ...prev, populares: false }));
    };

    setArticulo(null);
    setPopulares([]);

    fetchArticulo();
    fetchArticulosPopulares();
  }, [slug]);

  return (
    <main>
      {/* Hero */}
      <section className='!mt-[100px] px-4 md:px-10 lg:px-36 2xl:px-72 mb-6'>
        {
          cargando.articulo ?

            <div className='flex justify-start items-center gap-x-2'>
              <TextSkeleton className='!max-w-lg' />
              <TextSkeleton className='!max-w-lg invisible md:visible' />
              <TextSkeleton className='!max-w-lg invisible lg:visible' />
            </div>

            :

            <div className='!text-[16px] !font-[400] leading-[24px] w-full -mt-[55px] flex justify-start items-center bg-white z-10 relative gap-8'>
              <span className="rounded-lg !text-[var(--text-color-primary)] bg-[#dddddd] py-[5px] px-[22px] text-[16px] md:text-[18px] !font-[600]">
                {articulo?.etiqueta || "Digitalización"}
              </span>
              <span className="flex justify-start items-center gap-4 md:gap-5 pr-0 md:pr-4">
                <img src={GascoIcoClock} alt="Tiempo de lectura" className='inline-block' />
                <span className='!text-[var(--text-color-primary)] text-[16px] !font-[400]'>{articulo?.minutosLectura} min de lectura</span>
              </span>
            </div>
        }
        <div className='mt-12 pb-8'>
          <div>
            <h1 className='!text-[32px] md:!text-[36px] lg:!text-[40px] !font-[700] !leading-[38.4px] md:!leading-[43.2px] lg:!leading-[48px] text-[var(--text-color-secondary)]'>{articulo?.titulo}</h1>
            <p className='pt-4 !text-[16px] md:!text-[18px] lg:!text-[20px] !text-[var(--text-color-primary)] !font-[400] leading-[22.4px] md:leading-[25.2px] lg:leading-[28px]'>
              {articulo?.descripcion}
            </p>
          </div>
        </div>
        <div>
          <div className='mb-4'>
            <hr className='w-1/4 bg-gray-300 mb-8' />
            {
              cargando.articulo ?

                <TextSkeleton className='!max-w-lg' />

                :

                <p className='!text-[16px] md:!text-[18px] lg:!text-[20px] !font-[300] !text-[var(--text-color-primary)] leading-[22.4px] md:leading-[25.2px] lg:leading-[28px]'>
                  Publicado por {articulo?.autor}<br />
                  Categoría: {articulo?.categoria}<br />
                  {formatearFechaLarga(articulo?.fecha || '')}
                </p>
            }
          </div>
        </div>
      </section>
      {/* Cuerpo */}
      <section className='px-4 md:px-10 lg:px-36 2xl:px-72'>
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-1">
            <div className="flex flex-col gap-2">
              {/* Facebook */}
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} target='_blank' className='flex justify-start items-center'>
                <span className='w-[24px] h-auto'>
                  <svg focusable="false" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><path fill="#0a0a0a" d="M28 16c0-6.627-5.373-12-12-12S4 9.373 4 16c0 5.628 3.875 10.35 9.101 11.647v-7.98h-2.474V16H13.1v-1.58c0-4.085 1.849-5.978 5.859-5.978.76 0 2.072.15 2.608.298v3.325c-.283-.03-.775-.045-1.386-.045-1.967 0-2.728.745-2.728 2.683V16h3.92l-.673 3.667h-3.247v8.245C23.395 27.195 28 22.135 28 16"></path></svg>
                </span>
              </a>
              {/* Twitter / X */}
              <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}`} target='_blank' className='flex justify-start items-center'>
                <span className='w-[24px] h-auto'>
                  <svg focusable="false" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><path fill="#0a0a0a" d="M28 8.557a10 10 0 0 1-2.828.775 4.93 4.93 0 0 0 2.166-2.725 9.7 9.7 0 0 1-3.13 1.194 4.92 4.92 0 0 0-3.593-1.55 4.924 4.924 0 0 0-4.794 6.049c-4.09-.21-7.72-2.17-10.15-5.15a4.94 4.94 0 0 0-.665 2.477c0 1.71.87 3.214 2.19 4.1a5 5 0 0 1-2.23-.616v.06c0 2.39 1.7 4.38 3.952 4.83-.414.115-.85.174-1.297.174q-.476-.001-.928-.086a4.935 4.935 0 0 0 4.6 3.42 9.9 9.9 0 0 1-6.114 2.107q-.597 0-1.175-.068a13.95 13.95 0 0 0 7.55 2.213c9.056 0 14.01-7.507 14.01-14.013q0-.32-.015-.637c.96-.695 1.795-1.56 2.455-2.55z"></path></svg>
                </span>
              </a>
              {/* Email */}
              <a href={`mailto:?subject=${encodeURIComponent(articulo?.titulo || '')}&body=${encodeURIComponent(window.location.href)}`} target='_blank' className='flex justify-start items-center'>
                <span className='w-[24px] h-auto'>
                  <svg focusable="false" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><path fill="#0a0a0a" d="M27 21.775v-9.9s-10.01 6.985-10.982 7.348C15.058 18.878 5 11.875 5 11.875v9.9c0 1.375.293 1.65 1.65 1.65h18.7c1.393 0 1.65-.242 1.65-1.65m-.017-11.841c0-1.002-.291-1.359-1.633-1.359H6.65c-1.38 0-1.65.429-1.65 1.43l.016.154s9.939 6.842 11 7.216C17.14 16.941 27 10.005 27 10.005z"></path></svg>
                </span>
              </a>
              {/* Linkedin */}
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`} target='_blank' className='flex justify-start items-center'>
                <span className='w-[24px] h-auto'>
                  <svg focusable="false" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><path fill="#0a0a0a" d="M6.227 12.61h4.19v13.48h-4.19zm2.095-6.7a2.43 2.43 0 0 1 0 4.86c-1.344 0-2.428-1.09-2.428-2.43s1.084-2.43 2.428-2.43m4.72 6.7h4.02v1.84h.058c.56-1.058 1.927-2.176 3.965-2.176 4.238 0 5.02 2.792 5.02 6.42v7.395h-4.183v-6.56c0-1.564-.03-3.574-2.178-3.574-2.18 0-2.514 1.7-2.514 3.46v6.668h-4.187z"></path></svg>
                </span>
              </a>
              {/* Whatsapp */}
              <a href={`https://api.whatsapp.com/send?text=${encodeURIComponent(window.location.href)}`} target='_blank' className='flex justify-start items-center'>
                <span className='w-[24px] h-auto'>
                  <svg focusable="false" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><path fill="#0a0a0a" fillRule="evenodd" d="M16.21 4.41C9.973 4.41 4.917 9.465 4.917 15.7c0 2.134.592 4.13 1.62 5.832L4.5 27.59l6.25-2.002a11.24 11.24 0 0 0 5.46 1.404c6.234 0 11.29-5.055 11.29-11.29 0-6.237-5.056-11.292-11.29-11.292m0 20.69c-1.91 0-3.69-.57-5.173-1.553l-3.61 1.156 1.173-3.49a9.35 9.35 0 0 1-1.79-5.512c0-5.18 4.217-9.4 9.4-9.4s9.397 4.22 9.397 9.4c0 5.188-4.214 9.4-9.398 9.4zm5.293-6.832c-.284-.155-1.673-.906-1.934-1.012-.265-.106-.455-.16-.658.12s-.78.91-.954 1.096c-.176.186-.345.203-.628.048-.282-.154-1.2-.494-2.264-1.517-.83-.795-1.373-1.76-1.53-2.055s0-.445.15-.584c.134-.124.3-.326.45-.488.15-.163.203-.28.306-.47.104-.19.06-.36-.005-.506-.066-.147-.59-1.587-.81-2.173-.218-.586-.46-.498-.63-.505-.168-.007-.358-.038-.55-.045-.19-.007-.51.054-.78.332-.277.274-1.05.943-1.1 2.362-.055 1.418.926 2.826 1.064 3.023.137.2 1.874 3.272 4.76 4.537 2.888 1.264 2.9.878 3.43.85.53-.027 1.734-.633 2-1.297s.287-1.24.22-1.363c-.07-.123-.26-.203-.54-.357z" clipRule="evenodd"></path></svg>
                </span>
              </a>
              {/* Copiar enlace */}
              <button className='flex justify-start items-center' onClick={copiarEnlace}>
                <span className='w-[24px] h-auto'>
                  <svg focusable="false" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><path fill="#0a0a0a" d="M7.591 21.177q0-.54.377-.917l2.804-2.804a1.24 1.24 0 0 1 .913-.378q.565 0 .97.43-.038.041-.255.25-.215.21-.29.29a3 3 0 0 0-.2.256 1.1 1.1 0 0 0-.177.344 1.4 1.4 0 0 0-.046.37q0 .54.377.918a1.25 1.25 0 0 0 .918.377q.19.001.373-.047.189-.056.345-.175.135-.09.256-.2.15-.14.29-.29c.14-.142.223-.23.25-.254q.445.42.445.984 0 .539-.377.916l-2.778 2.79a1.24 1.24 0 0 1-.917.364q-.54-.001-.917-.35l-1.982-1.97a1.22 1.22 0 0 1-.378-.9zm9.477-9.504q0-.54.377-.917l2.777-2.79a1.24 1.24 0 0 1 .913-.378q.525-.001.917.364l1.984 1.968q.38.378.38.903 0 .54-.38.917l-2.802 2.804a1.24 1.24 0 0 1-.916.364q-.565 0-.97-.418.038-.04.255-.25a8 8 0 0 0 .29-.29q.108-.12.2-.255.121-.156.176-.344.048-.181.047-.37 0-.538-.377-.914a1.25 1.25 0 0 0-.917-.377q-.205 0-.37.046-.172.046-.346.175a4 4 0 0 0-.256.2q-.08.076-.29.29l-.25.258q-.441-.417-.442-.983zM5.003 21.177q0 1.617 1.146 2.736l1.982 1.968c.745.75 1.658 1.12 2.736 1.12q1.63 0 2.75-1.143l2.777-2.79c.75-.747 1.12-1.66 1.12-2.737q.002-1.66-1.183-2.818l1.186-1.185q1.16 1.185 2.805 1.186 1.617 0 2.75-1.13l2.803-2.81q1.127-1.132 1.128-2.748 0-1.62-1.146-2.738L23.875 6.12Q22.758 4.999 21.139 5q-1.63 0-2.75 1.146l-2.777 2.79c-.75.747-1.12 1.66-1.12 2.737q-.002 1.658 1.183 2.817l-1.186 1.186q-1.16-1.186-2.805-1.186-1.617 0-2.75 1.132L6.13 18.426Q5 19.559 5 21.176z"></path></svg>
                </span>
              </button>
            </div>
          </div>
          <div className="col-span-12 md:col-span-11 md:mt-6 overflow-hidden">
            {
              cargando.articulo ?

                <div>
                  <TextSkeleton className='!max-w-lg mb-8' />
                  <ImageSkeleton className='w-full h-[300px] rounded-lg mb-8' />
                  <TextSkeleton className='!max-w-2xl mb-8' />
                </div>

                :

                articulo?.contenido.map((item, index) => (
                  <div key={index} className="mb-6">
                    {
                      item.__component === tipoContenido.textoEnriquecido && (
                        <div className="!text-[16px] md:!text-[18px] lg:!text-[20px] !font-[400] !leading-[24px] md:!leading-[25.2px] lg:!leading-[28px] text-[var(--text-color-primary)]">
                          {
                            item.contenido?.map((contenido: Record<string, any>, cIndex: number) => {

                              if (contenido.type === 'paragraph') {
                                return (<p className="mb-4" key={`contenido-${cIndex}`}>
                                  {
                                    contenido.children.map((child: Record<string, any>, childIndex: number) => {
                                      if (child.type === 'text' && !child.bold && !child.italic) {
                                        return <span key={`child-${cIndex}-${childIndex}`} >{child.text}</span>;
                                      }

                                      else if (child.type === 'text' && child.bold) {
                                        return <span key={`child-${cIndex}-${childIndex}`} className="!font-bold">{child.text}</span>;
                                      }

                                      else if (child.type === 'text' && child.italic) {
                                        return <span key={`child-${cIndex}-${childIndex}`} className="!italic">{child.text}</span>;
                                      }

                                      else if (child.type === 'text' && child.bold && child.italic) {
                                        return <span key={`child-${cIndex}-${childIndex}`} className="!font-bold !italic">{child.text}</span>;
                                      }

                                      else if (child.type === 'link') {
                                        return (
                                          <a key={`child-${cIndex}-${childIndex}`} href={child.url} className="!text-wrap !text-[#0693e3] !font-bold">
                                            {child.children[0]?.text || ''}
                                          </a>
                                        );
                                      }

                                      else return null;
                                    })
                                  }
                                </p>)
                              }

                              else if (contenido.type === 'list' && contenido.format === 'unordered') {
                                return (
                                  <ul key={`item-${cIndex}`} className='list-disc pl-5 mb-4 !apearance-none'>
                                    {
                                      contenido.children.map((listItem: any, listIndex: number) => (
                                        <li key={`item-${cIndex}-${listIndex}`} className='!text-[16px] md:!text-[18px] lg:!text-[20px] !font-[400] !leading-[24px] md:!leading-[25.2px] lg:!leading-[28px] text-[var(--text-color-primary)]'>
                                          <span>
                                            {listItem.children.map((child: Record<string, any>, childIndex: number) => {
                                              if (child.type === 'text') {
                                                return child.bold ? 
                                                  <span key={`child-${cIndex}-${listIndex}-${childIndex}`} className="!font-bold">{child.text}</span> :
                                                  <span key={`child-${cIndex}-${listIndex}-${childIndex}`}>{child.text}</span>;
                                              }

                                              else if (child.type === 'link') {
                                                return (
                                                  <a key={`child-${cIndex}-${listIndex}-${childIndex}`} href={child.url} target='_blank' className="!text-[#0693e3] !font-bold">
                                                    {child.children[0]?.text || ''}
                                                  </a>
                                                );
                                              }
                                            })}
                                          </span>
                                        </li>
                                      ))
                                    }
                                  </ul>
                                )
                              }

                              else if (contenido.type === 'list' && contenido.format === 'ordered') {
                                return (
                                  <ol key={`item-${cIndex}`} className='list-decimal pl-5 mb-4'>
                                    {
                                      contenido.children.map((listItem: any, listIndex: number) => (
                                        <li key={`item-${cIndex}-${listIndex}`} className='!text-[16px] md:!text-[18px] lg:!text-[20px] !font-[400] !leading-[24px] md:!leading-[25.2px] lg:!leading-[28px] text-[var(--text-color-primary)]'>
                                          <span>
                                            {listItem.children.map((child: Record<string, any>, childIndex: number) => {
                                              if (child.type === 'text') {
                                                return child.bold ? 
                                                  <span key={`child-${cIndex}-${listIndex}-${childIndex}`} className="!font-bold">{child.text}</span> :
                                                  <span key={`child-${cIndex}-${listIndex}-${childIndex}`}>{child.text}</span>;
                                              }

                                              else if (child.type === 'link') {
                                                return (
                                                  <a key={`child-${cIndex}-${listIndex}-${childIndex}`} href={child.url} className="!text-[#0693e3] !font-bold">
                                                    {child.children[0]?.text || ''}
                                                  </a>
                                                );
                                              }
                                            })}
                                          </span>
                                        </li>
                                      ))
                                    }
                                  </ol>
                                )
                              }

                              else if (contenido.type === 'heading') {
                                if (contenido.level === 2) {
                                  return (
                                    <h2 className="mb-4 !text-[24px] !font-[700] leading-[32px] text-[var(--text-color-secondary)]" key={`contenido-${cIndex}`}>
                                      {
                                        contenido.children.map((child: Record<string, any>, childIndex: number) => {
                                          if (child.type === 'text') {
                                            return <span key={`child-${cIndex}-${childIndex}`}>{child.text}</span>;
                                          }
                                          return null;
                                        })
                                      }
                                    </h2>
                                  );
                                }

                                else if (contenido.level === 3) {
                                  return (
                                    <h3 className="mb-4 !text-[20px] !font-[700] leading-[28px] text-[var(--text-color-secondary)]" key={`contenido-${cIndex}`}>
                                      {
                                        contenido.children.map((child: Record<string, any>, childIndex: number) => {
                                          if (child.type === 'text') {
                                            return <span key={`child-${cIndex}-${childIndex}`}>{child.text}</span>;
                                          }
                                          return null;
                                        })
                                      }
                                    </h3>
                                  );
                                }
                              }
                              
                            })
                          }
                        </div>
                      )
                    }
                    {
                      item.__component === tipoContenido.imagen && (
                        <div className="mb-6">
                          <img src={item.imagen.url} alt={item.imagen.alternativeText || 'Imagen'} className="w-full h-auto" />
                        </div>
                      )
                    }
                  </div>
                ))
            }
          </div>
        </div>
      </section>
      {/* Contáctanos */}
      <section className='px-4 md:px-10 lg:px-36 2xl:px-72 !pt-[32px] pb-32'>
        <div className='w-full bg-[rgba(221,221,221,0.3)] rounded-lg p-[16px] xl:px-12'>
          <h2 className='!text-[24px] md:text-[2.25rem] lg:text-[2.5rem] text-center leading-[28.8px] md:leading-[48px] font-bold text-[var(--text-color-secondary)] !my-[16px]'>
            Contacta a nuestros ejecutivos
          </h2>
          <p className='text-[1rem] md:text-[1.125rem] lg:text-[1.25rem] text-center md:text-left xl:text-center !leading-[22px] lg:leading-8 xl:!leading-[28px] text-[var(--text-color-primary)] mb-4 2xl:mb-8'>
            Trabajamos para ofrecer soluciones energéticas innovadoras y de acuerdo a las necesidades de cada uno de nuestros clientes.
          </p>
          <div className="flex justify-center">
            <NavLink to="/contacto" className="!text-[15px] md:text-base !text-white !decoration-none !font-[600] !rounded-lg !bg-[var(--background-color-primary)] px-6 py-2 w-full h-full max-w-36 lg:max-w-64 flex justify-center items-center">
              Contáctanos
            </NavLink>
          </div>
        </div>
      </section>
      {/* Artículos populares */}
      <section className='px-4 md:px-10 lg:px-36 2xl:px-72 pb-20'>
        <div className='md:mb-8'>
          <h2 className='uppercase !text-[var(--text-color-secondary)] !text-[24px] !font-[700]'>Artículos más populares</h2>
        </div>
        <div className="grid grid-cols-12 gap-4 md:gap-x-8 md:gap-y-16 pb-10">
          {
            cargando.populares ?

              Array.from({ length: 2 }).map((_, index) => (
                <article key={`popular-skeleton-${index}`} className='col-span-12 md:col-span-6 flex flex-col justify-end'>
                  <ImageSkeleton className='w-full h-[200px] rounded-lg mb-4' />
                  <TextSkeleton className='!max-w-lg mb-2' />
                </article>
              ))

              :

              populares.map((popular: Articulo) => (
                <article key={`articulo-${popular.id}`} className='col-span-12 md:col-span-6 flex flex-col justify-end'>
                  <div className='h-full'>
                    <NavLink className="pb-4" to={`/articulo/${popular.slug}`}>
                      <figure className='!m-0 !p-0 w-full h-[200px] xl:h-[315px] 2xl:h-[550px]'>
                        <img src={popular.portadaUrl} className='object-cover w-full h-full' alt="" />
                      </figure>
                    </NavLink>
                    <div className="flex justify-between md:justify-start md:gap-4 items-center mt-4">
                      <span className="rounded-lg !text-[var(--text-color-primary)] bg-[#dddddd] py-[6px] px-[20px] text-[13px] xl:text-[16px] !font-[600]">
                        {popular.etiqueta || "Digitalización"}
                      </span>
                      <span className="flex justify-start items-center gap-4 md:gap-2 pr-4">
                        <img src={GascoIcoClock} alt="Tiempo de lectura" className='inline-block' />
                        <span className='!text-[var(--text-color-primary)] text-[16px] !font-[600] text-ellipsis overflow-hidden whitespace-nowrap'>{popular.minutosLectura} min de lectura</span>
                      </span>
                    </div>
                    <NavLink className="!text-[var(--text-color-secondary)] text-[22px]" to={`/articulo/${popular.slug}`}>
                      <h3 className='!font-[400] leading-7'>{popular.titulo}</h3>
                    </NavLink>
                    <span className='text-[12.8px] text-[rgb(100,102,101)] outline-[rgb(100,102,101)] block pt-4 pb-6'>{formatearFechaLarga(popular.fecha)}</span>
                  </div>
                  <NavLink className='text-[16px] !font-[700] underline underline-offset-8' to={`/articulo/${popular.slug}`}>
                    <span>Ir a ver el artículo</span>
                    <img src={GascoIcoRightBlue} alt="Ir a ver el artículo" className='inline-block ml-4 w-[32px]' />
                  </NavLink>
                </article>
              ))
          }
        </div>
      </section>
    </main>
  )
}

export default DetalleArticulo